<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white px-4 py-4 flex items-center justify-between border-b border-gray-200 shadow-sm">
      <img
        src="/kctLogo.webp"
        alt="KCT"
        class="h-10"
      />
      <button
        v-if="!showProjects && job"
        @click="loadProjects"
        class="px-4 h-9 rounded-lg flex items-center text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
      >
        ALL MY BOOKINGS<i class="fa-solid fa-chevron-right ml-2 text-xs"></i>
      </button>
      <button
        v-if="showProjects"
        @click="showProjects = false"
        class="px-4 h-9 rounded-lg flex items-center text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
      >
        <i class="fa-solid fa-chevron-left mr-2 text-xs"></i>Back
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-20">
      <i class="fa-solid fa-spinner fa-spin text-3xl text-gray-400"></i>
    </div>

    <!-- Error -->
    <div v-else-if="errorMsg" class="px-4 py-12 text-center">
      <i class="fa-solid fa-circle-exclamation text-red-400 text-4xl mb-4"></i>
      <p class="text-gray-600 text-sm">{{ errorMsg }}</p>
    </div>

    <!-- Projects List -->
    <div v-else-if="showProjects" class="max-w-lg mx-auto">
      <div class="px-4 py-4">
        <h2 class="text-base font-semibold text-gray-900 mb-1">Your Projects</h2>
        <p class="text-xs text-gray-400 mb-4">Active jobs for {{ job.contact || job.company || 'your account' }}</p>
      </div>

      <div v-if="projectsLoading" class="flex items-center justify-center py-12">
        <i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400"></i>
      </div>

      <div v-else-if="projects.length" class="px-4 space-y-3 pb-6">
        <div
          v-for="project in projects"
          :key="project.token"
          @click="goToProject(project)"
          class="bg-white rounded-xl border p-4 cursor-pointer transition-colors"
          :class="project.current ? 'border-primary bg-green-50' : 'border-gray-200 hover:border-gray-300'"
        >
          <div class="flex items-start justify-between mb-2">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900 truncate">{{ project.projectName || project.company || 'Untitled' }}</p>
              <p v-if="project.projectName && project.company" class="text-xs text-gray-500 truncate">{{ project.company }}</p>
            </div>
            <span
              v-if="project.current"
              class="ml-2 flex-shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-primary text-white"
            >Current</span>
            <span
              v-else-if="project.clientSmsStatus === 'FIRM'"
              class="ml-2 flex-shrink-0 text-[10px] font-medium uppercase px-2 py-0.5 rounded-full bg-green-100 text-green-700"
            >Confirmed</span>
            <span
              v-else-if="project.status"
              class="ml-2 flex-shrink-0 text-[10px] font-medium uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
            >{{ project.status }}</span>
          </div>
          <div class="space-y-1">
            <div v-if="project.formattedAddress" class="flex items-start text-xs text-gray-500">
              <i class="fa-solid fa-location-dot w-4 mt-0.5 text-gray-400"></i>
              <span class="ml-1 truncate">{{ project.formattedAddress }}</span>
            </div>
            <div v-if="project.date" class="flex items-start text-xs text-gray-500">
              <i class="fa-regular fa-calendar w-4 mt-0.5 text-gray-400"></i>
              <span class="ml-1">{{ formatProjectDate(project.date) }}</span>
            </div>
            <div v-if="project.meters" class="flex items-start text-xs text-gray-500">
              <i class="fa-solid fa-ruler w-4 mt-0.5 text-gray-400"></i>
              <span class="ml-1">{{ project.meters }}m³</span>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="px-4 py-12 text-center">
        <i class="fa-solid fa-folder-open text-gray-300 text-3xl mb-3"></i>
        <p class="text-sm text-gray-500">No other active projects found.</p>
      </div>
    </div>

    <!-- Job Content -->
    <div v-else-if="job" class="max-w-lg mx-auto">
      <!-- Status Banner -->
      <div :class="statusBannerClass" class="px-4 py-3 flex items-center">
        <i :class="statusIcon" class="mr-2 text-lg"></i>
        <span class="font-semibold">{{ statusLabel }}</span>
      </div>

      <!-- Progress Stepper -->
      <div class="bg-white px-4 py-4 border-b border-gray-100">
        <div class="flex items-center justify-between">
          <div
            v-for="(step, idx) in steps"
            :key="idx"
            class="flex flex-col items-center flex-1"
          >
            <div class="relative flex items-center justify-center w-full">
              <div
                v-if="idx > 0"
                class="absolute left-0 right-1/2 top-1/2 h-0.5 -translate-y-1/2"
                :class="idx <= activeStep ? 'bg-primary' : 'bg-gray-200'"
              ></div>
              <div
                v-if="idx < steps.length - 1"
                class="absolute left-1/2 right-0 top-1/2 h-0.5 -translate-y-1/2"
                :class="idx < activeStep ? 'bg-primary' : 'bg-gray-200'"
              ></div>
              <div
                class="relative z-10 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                :class="idx <= activeStep ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'"
              >
                <i v-if="idx < activeStep" class="fa-solid fa-check text-xs"></i>
                <span v-else>{{ idx + 1 }}</span>
              </div>
            </div>
            <span class="text-[10px] mt-1 text-center leading-tight" :class="idx <= activeStep ? 'text-primary font-semibold' : 'text-gray-400'">
              {{ step }}
            </span>
          </div>
        </div>
      </div>

      <!-- Live Map (when tracking available) -->
      <div v-if="tracking && tracking.tracking" class="bg-white border-b border-gray-100">
        <div id="client-map" class="w-full h-56"></div>
        <div class="px-4 py-2 flex items-center text-sm text-gray-600">
          <div class="w-2.5 h-2.5 rounded-full bg-primary mr-2 animate-pulse"></div>
          <span>{{ tracking.testerName }} is en route</span>
        </div>
      </div>

      <!-- En-route but no vehicle -->
      <div
        v-else-if="tracking && !tracking.tracking && tracking.reason === 'no-vehicle' && isEnRoute"
        class="bg-white border-b border-gray-100 px-4 py-4 flex items-center"
      >
        <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
          <i class="fa-solid fa-truck text-blue-600"></i>
        </div>
        <div>
          <div class="font-semibold text-sm text-gray-800">{{ tracking.testerName }}</div>
          <span class="inline-block mt-0.5 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">En Route</span>
        </div>
      </div>

      <!-- Job Details Card -->
      <div class="bg-white px-4 py-4 border-b border-gray-100">
        <h3 class="text-sm font-bold text-gray-800 mb-3">
          <i class="fa-solid fa-clipboard-list text-primary mr-2"></i>Job Details
        </h3>
        <div class="space-y-2">
          <div v-if="jobDate" class="flex items-start text-sm">
            <i class="fa-regular fa-calendar text-gray-400 w-5 mt-0.5"></i>
            <span class="text-gray-700 ml-2">{{ jobDate }}</span>
          </div>
          <div v-if="jobTime" class="flex items-start text-sm">
            <i class="fa-regular fa-clock text-gray-400 w-5 mt-0.5"></i>
            <span class="text-gray-700 ml-2">{{ jobTime }}</span>
          </div>
          <div v-if="job.formattedAddress || jobAddress" class="flex items-start text-sm">
            <i class="fa-solid fa-location-dot text-gray-400 w-5 mt-0.5"></i>
            <span class="text-gray-700 ml-2">{{ job.formattedAddress || jobAddress }}</span>
          </div>
          <div v-if="job.company" class="flex items-start text-sm">
            <i class="fa-solid fa-building text-gray-400 w-5 mt-0.5"></i>
            <span class="text-gray-700 ml-2">{{ job.company }}</span>
          </div>
          <template v-if="customFields.length">
            <div v-for="f in customFields" :key="f.key" class="flex items-start text-sm">
              <i class="fa-solid fa-circle-info text-gray-400 w-5 mt-0.5"></i>
              <span class="text-gray-700 ml-2">{{ f.label }}: {{ f.display }}</span>
            </div>
          </template>
          <template v-else-if="!customFieldsEnabled">
            <div v-if="job.meters" class="flex items-start text-sm">
              <i class="fa-solid fa-ruler text-gray-400 w-5 mt-0.5"></i>
              <span class="text-gray-700 ml-2">{{ job.meters }}m³</span>
            </div>
            <div v-if="job.mixCode" class="flex items-start text-sm">
              <i class="fa-solid fa-flask text-gray-400 w-5 mt-0.5"></i>
              <span class="text-gray-700 ml-2">{{ job.mixCode }}</span>
            </div>
          </template>
          <div v-if="job.requirements" class="flex items-start text-sm">
            <i class="fa-solid fa-list-check text-gray-400 w-5 mt-0.5"></i>
            <span class="text-gray-700 ml-2">{{ job.requirements }}</span>
          </div>
          <div v-if="!customFieldsEnabled && job.pourLocation" class="flex items-start text-sm">
            <i class="fa-solid fa-map-pin text-gray-400 w-5 mt-0.5"></i>
            <span class="text-gray-700 ml-2">{{ job.pourLocation }}</span>
          </div>
          <div v-if="job.duration" class="flex items-start text-sm">
            <i class="fa-regular fa-clock text-gray-400 w-5 mt-0.5"></i>
            <span class="text-gray-700 ml-2">{{ job.duration }}</span>
          </div>
          <div v-if="job.contractor && job.contractor.name" class="flex items-start text-sm">
            <i class="fa-solid fa-user-helmet-safety text-gray-400 w-5 mt-0.5"></i>
            <span class="text-gray-700 ml-2">{{ job.contractor.name }}</span>
          </div>
        </div>
      </div>

      <!-- Test Reports -->
      <div v-if="testReports.length" class="bg-white px-4 py-4 border-b border-gray-100">
        <h3 class="text-sm font-bold text-gray-800 mb-3">
          <i class="fa-solid fa-flask-vial text-primary mr-2"></i>Test Reports
        </h3>
        <div class="space-y-2">
          <div v-for="report in testReports" :key="report.id">
            <p v-if="report.subject" class="text-xs text-gray-500 mb-1">{{ report.subject }}</p>
            <div v-for="(att, idx) in report.attachments" :key="idx">
              <a
                :href="att.downloadUrl"
                target="_blank"
                class="flex items-center text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2.5 transition-colors"
              >
                <i class="fa-solid fa-file-pdf text-red-500 mr-2.5"></i>
                <span class="flex-1 truncate">{{ att.filename }}</span>
                <i class="fa-solid fa-download text-gray-400 ml-2 text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Status badge -->
      <div v-if="job.clientSmsStatus === 'FIRM'" class="px-4 pt-4">
        <div class="w-full rounded-lg bg-green-50 border border-green-200 py-3 flex items-center justify-center">
          <i class="fa-solid fa-circle-check text-green-600 mr-2"></i>
          <span class="text-green-700 font-semibold text-sm">Job Confirmed</span>
        </div>
      </div>
      <div v-else-if="job.clientSmsStatus === 'CANCELLED'" class="px-4 pt-4">
        <div class="w-full rounded-lg bg-red-50 border border-red-200 py-3 flex items-center justify-center">
          <i class="fa-solid fa-circle-xmark text-red-600 mr-2"></i>
          <span class="text-red-700 font-semibold text-sm">Job Cancelled</span>
        </div>
      </div>
      <div v-else-if="job.clientSmsStatus === 'CHANGED'" class="px-4 pt-4">
        <div class="w-full rounded-lg bg-yellow-50 border border-yellow-200 py-3 flex items-center justify-center">
          <i class="fa-solid fa-circle-question text-yellow-600 mr-2"></i>
          <span class="text-yellow-700 font-semibold text-sm">Change Requested</span>
        </div>
      </div>

      <!-- Action Buttons — always visible -->
      <div class="px-4 py-4 space-y-3">
        <!-- Confirm Job -->
        <button
          @click="confirmJob"
          :disabled="actionLoading"
          class="w-full rounded-lg bg-primary text-white font-semibold py-4 text-base hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
        >
          <i v-if="actionLoading && actionType === 'confirm'" class="fa-solid fa-spinner fa-spin mr-2"></i>
          <i v-else class="fa-solid fa-check mr-2"></i>
          Confirm Job
        </button>

        <!-- Request Change -->
        <button
          @click="changeModal = true"
          class="w-full rounded-lg border border-gray-300 text-gray-700 font-semibold py-4 text-base hover:bg-gray-50 flex items-center justify-center"
        >
          <i class="fa-solid fa-pen mr-2"></i>
          Request Change
        </button>

        <!-- Cancel Job -->
        <button
          @click="showCancelConfirm = true"
          :disabled="actionLoading"
          class="w-full rounded-lg bg-red-600 text-white font-semibold py-4 text-base hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
        >
          <i v-if="actionLoading && actionType === 'cancel'" class="fa-solid fa-spinner fa-spin mr-2"></i>
          <i v-else class="fa-solid fa-ban mr-2"></i>
          Cancel Job
        </button>
      </div>

      <!-- Toast -->
      <div
        v-if="toast"
        class="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50"
      >
        {{ toast }}
      </div>
    </div>

    <!-- Cancel Confirmation Modal -->
    <div v-if="showCancelConfirm" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-gray-900 opacity-60" @click="showCancelConfirm = false"></div>
      <div class="bg-white z-10 rounded-xl shadow-xl p-6 w-full max-w-sm mx-4 relative">
        <div class="flex items-center justify-center mb-4">
          <div class="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <i class="fa-solid fa-triangle-exclamation text-red-500 text-2xl"></i>
          </div>
        </div>
        <h3 class="text-lg font-bold text-gray-900 text-center mb-2">Are you sure?</h3>
        <p class="text-sm text-gray-500 text-center mb-6">This will cancel your job and notify KCT. You can still confirm or request changes later.</p>
        <button
          @click="cancelJob"
          :disabled="actionLoading"
          class="w-full rounded-lg bg-red-600 text-white font-semibold py-3 text-sm hover:bg-red-700 disabled:opacity-50 flex items-center justify-center mb-3"
        >
          <i v-if="actionLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
          Yes, Cancel Job
        </button>
        <button
          @click="showCancelConfirm = false"
          class="w-full rounded-lg border border-gray-300 text-gray-700 font-medium py-3 text-sm hover:bg-gray-50"
        >
          Go Back
        </button>
      </div>
    </div>

    <!-- Change Modal (full-screen) -->
    <div v-if="changeModal" class="fixed inset-0 z-50 bg-white flex flex-col">
      <!-- Modal header -->
      <div class="bg-secondary px-4 py-3 flex items-center">
        <button @click="changeModal = false" class="text-white mr-3">
          <i class="fa-solid fa-arrow-left text-lg"></i>
        </button>
        <span class="text-white font-semibold">Request Change</span>
      </div>

      <div class="flex-1 px-4 py-6 flex flex-col max-w-lg mx-auto w-full">
        <label class="text-sm text-gray-600 mb-2">Describe the change you need:</label>
        <textarea
          v-model="changeMessage"
          rows="5"
          class="rounded-lg border border-gray-300 p-3 text-sm text-gray-700 w-full outline-none focus:border-primary mb-4 flex-shrink-0"
          placeholder="e.g. Change delivery time to 9am, change meters to 5m³..."
        ></textarea>

        <button
          @click="sendChange"
          :disabled="!changeMessage.trim() || actionLoading"
          class="w-full rounded-lg bg-primary text-white font-semibold py-3 text-sm hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
        >
          <i v-if="actionLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
          <i v-else class="fa-solid fa-paper-plane mr-2"></i>
          Send Change Request
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

const API_BASE = 'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/public/job';
const MAPBOX_TOKEN = 'pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw';

export default {
  name: 'ClientJobView',
  data() {
    return {
      token: '',
      job: null,
      loading: true,
      errorMsg: '',
      tracking: null,
      trackingInterval: null,
      map: null,
      marker: null,
      changeModal: false,
      changeMessage: '',
      actionLoading: false,
      actionType: '',
      showCancelConfirm: false,
      toast: '',
      showProjects: false,
      projects: [],
      projectsLoading: false,
      testReports: [],
    };
  },
  computed: {
    // The backend only sends customFieldDefs when the feature is enabled, so
    // its absence means "render the legacy hardcoded rows".
    customFieldsEnabled() {
      return Array.isArray(this.job?.customFieldDefs);
    },
    customFields() {
      if (!this.customFieldsEnabled) return [];
      return this.job.customFieldDefs
        .map((f) => ({ ...f, display: this.fieldDisplay(f) }))
        .filter((f) => f.display !== null);
    },
    statusBannerClass() {
      const s = this.job?.clientSmsStatus;
      if (s === 'FIRM') return 'bg-green-100 text-green-800';
      if (s === 'CANCELLED') return 'bg-red-100 text-red-800';
      if (s === 'CHANGED') return 'bg-yellow-100 text-yellow-800';
      const st = this.job?.status;
      if (st === 'COMMITTED') return 'bg-blue-100 text-blue-800';
      return 'bg-yellow-100 text-yellow-800';
    },
    statusIcon() {
      const s = this.job?.clientSmsStatus;
      if (s === 'FIRM') return 'fa-solid fa-circle-check';
      if (s === 'CANCELLED') return 'fa-solid fa-circle-xmark';
      if (s === 'CHANGED') return 'fa-solid fa-triangle-exclamation';
      return 'fa-solid fa-clock';
    },
    statusLabel() {
      const s = this.job?.clientSmsStatus;
      if (s === 'FIRM') return 'Confirmed';
      if (s === 'CANCELLED') return 'Cancelled';
      if (s === 'CHANGED') return 'Change Requested';
      const st = this.job?.status;
      if (st === 'COMMITTED') return 'Tester Assigned';
      if (st === 'ON-CALL') return 'On Call';
      return 'Scheduled';
    },
    steps() {
      return ['Scheduled', 'Confirmed', 'En Route', 'Testing', 'Complete'];
    },
    activeStep() {
      const s = this.job?.clientSmsStatus;
      const st = this.job?.status;
      if (st === 'COMPLETED' || st === 'COMPLETE') return 4;
      if (st === 'TESTING' || st === 'IN_PROGRESS') return 3;
      if (this.isEnRoute) return 2;
      if (s === 'FIRM') return 1;
      return 0;
    },
    isEnRoute() {
      const st = this.job?.status;
      return st === 'COMMITTED' || st === 'EN_ROUTE' || st === 'EN-ROUTE';
    },
    jobDate() {
      if (!this.job?.date) return '';
      const d = new Date(this.job.date);
      if (isNaN(d.getTime())) return this.job.date;
      return d.toLocaleDateString('en-AU', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    },
    jobTime() {
      if (!this.job?.date) return '';
      const d = new Date(this.job.date);
      if (isNaN(d.getTime())) return '';
      const h = d.getHours();
      const m = d.getMinutes();
      if (h === 0 && m === 0) return '';
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    },
    jobAddress() {
      const a = this.job?.address;
      if (!a) return '';
      return [a.street, a.suburb, a.state, a.postcode].filter(Boolean).join(', ');
    },
  },
  watch: {
    '$route.params.token': {
      async handler(newToken) {
        if (!newToken || newToken === this.token) return;
        this.token = newToken;
        this.showProjects = false;
        this.projects = [];
        this.job = null;
        this.tracking = null;
        this.testReports = [];
        this.errorMsg = '';
        this.loading = true;
        if (this.trackingInterval) clearInterval(this.trackingInterval);
        if (this.map) { this.map.remove(); this.map = null; this.marker = null; }
        await this.fetchJob();
        if (this.job) {
          this.pollTracking();
          this.trackingInterval = setInterval(() => this.pollTracking(), 10000);
        }
      },
    },
  },
  async mounted() {
    this.token = this.$route.params.token;
    await this.fetchJob();
    if (this.job) {
      this.pollTracking();
      this.trackingInterval = setInterval(() => this.pollTracking(), 10000);
    }
  },
  beforeDestroy() {
    if (this.trackingInterval) clearInterval(this.trackingInterval);
    if (this.map) this.map.remove();
  },
  methods: {
    // null means "don't show this row at all".
    fieldDisplay(f) {
      const v = this.job[f.key];
      if (f.type === 'checkbox') return v === true ? 'Yes' : null;
      if (v === undefined || v === null || v === '') return null;
      return v;
    },
    async fetchJob() {
      try {
        const res = await axios.get(`${API_BASE}/${this.token}`);
        this.job = res.data.data;
        this.fetchTestReports();
      } catch (e) {
        this.errorMsg = e.response?.status === 403
          ? 'This link is invalid or has expired.'
          : 'Unable to load job details. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async fetchTestReports() {
      try {
        const jobId = this.token.split('~')[0];
        if (!jobId) return;
        const snap = await getDocs(
          query(
            collection(db, 'incomingFiles'),
            where('jobId', '==', jobId),
            where('status', '==', 'allocated')
          )
        );
        this.testReports = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error('Failed to load test reports:', e);
        this.testReports = [];
      }
    },
    async pollTracking() {
      try {
        const res = await axios.get(`${API_BASE}/${this.token}/tracking`);
        this.tracking = res.data.data;
        if (this.tracking?.tracking && this.tracking.latitude && this.tracking.longitude) {
          this.$nextTick(() => this.updateMap(this.tracking.latitude, this.tracking.longitude));
        }
      } catch (e) {
        // Silently ignore tracking errors
      }
    },
    updateMap(lat, lng) {
      if (!this.map) {
        const container = document.getElementById('client-map');
        if (!container) return;
        mapboxgl.accessToken = MAPBOX_TOKEN;
        this.map = new mapboxgl.Map({
          container: 'client-map',
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [lng, lat],
          zoom: 14,
          interactive: false,
        });

        // Pulsing marker
        const el = document.createElement('div');
        el.style.cssText = 'position:relative;width:44px;height:44px;';

        const dot = document.createElement('div');
        dot.style.cssText = `
          position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
          width:16px;height:16px;border-radius:50%;background:#01BC38;
          border:2px solid #fff;box-shadow:0 0 6px rgba(0,0,0,0.3);
        `;
        el.appendChild(dot);

        const pulse = document.createElement('div');
        pulse.style.cssText = `
          position:absolute;top:0;left:0;width:44px;height:44px;
          border-radius:50%;border:2px solid #01BC38;
          animation:marker-pulse-ring 2s ease-out infinite;
          pointer-events:none;
        `;
        el.appendChild(pulse);

        this.marker = new mapboxgl.Marker({ element: el })
          .setLngLat([lng, lat])
          .addTo(this.map);
      } else {
        this.marker.setLngLat([lng, lat]);
        this.map.flyTo({ center: [lng, lat], duration: 1000 });
      }
    },
    async confirmJob() {
      this.actionType = 'confirm';
      this.actionLoading = true;
      try {
        await axios.post(`${API_BASE}/${this.token}/sms`, { type: 'confirm' });
        this.job.clientSmsStatus = 'FIRM';
        this.showToast('Job confirmed!');
      } catch (e) {
        this.showToast('Failed to confirm. Please try again.');
      } finally {
        this.actionLoading = false;
        this.actionType = '';
      }
    },
    async sendChange() {
      this.actionType = 'change';
      this.actionLoading = true;
      try {
        await axios.post(`${API_BASE}/${this.token}/sms`, {
          type: 'change',
          message: this.changeMessage,
        });
        this.job.clientSmsStatus = 'CHANGED';
        this.changeModal = false;
        this.changeMessage = '';
        this.showToast('Change request sent!');
      } catch (e) {
        this.showToast('Failed to send. Please try again.');
      } finally {
        this.actionLoading = false;
        this.actionType = '';
      }
    },
    async cancelJob() {
      this.actionType = 'cancel';
      this.actionLoading = true;
      try {
        await axios.post(`${API_BASE}/${this.token}/sms`, { type: 'cancel' });
        this.job.clientSmsStatus = 'CANCELLED';
        this.showCancelConfirm = false;
        this.changeModal = false;
        this.showToast('Job cancelled.');
      } catch (e) {
        this.showToast('Failed to cancel. Please try again.');
      } finally {
        this.actionLoading = false;
        this.actionType = '';
      }
    },
    async loadProjects() {
      this.showProjects = true;
      this.projectsLoading = true;
      try {
        const res = await axios.get(`${API_BASE}/${this.token}/projects`);
        this.projects = res.data.data || [];
      } catch (e) {
        this.projects = [];
      } finally {
        this.projectsLoading = false;
      }
    },
    goToProject(project) {
      if (project.current) {
        this.showProjects = false;
        return;
      }
      this.$router.push({ name: 'ClientJobView', params: { token: project.token } });
    },
    formatProjectDate(date) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      return d.toLocaleDateString('en-AU', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    showToast(msg) {
      this.toast = msg;
      setTimeout(() => { this.toast = ''; }, 3000);
    },
  },
};
</script>

<style scoped>
.bg-primary { background-color: #01BC38; }
.text-primary { color: #01BC38; }
.border-primary { border-color: #01BC38; }
.focus\:border-primary:focus { border-color: #01BC38; }
.bg-secondary { background-color: #293a4c; }

@keyframes marker-pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}
</style>
